


import { Component, forwardRef, Inject, Input, SimpleChanges, OnChanges, OnInit } from '@angular/core';
import { CognitoService } from '../service/cognito.service';
import { TranslateService } from '@ngx-translate/core';
import { ThemeService } from '../service/theme.service';
import { ClientService } from '../service/client.service';

// Interface for device to display on the map,
interface DevicesLocation {
  thingName: string,
  address: string,
  gps: string,
  clientId?: string,
  subGroup?: string
}

@Component({
    selector: 'app-devices-map',
    templateUrl: './devices-map.component.html',
    styleUrls: ['./devices-map.component.css'],
    standalone: false
})

export class DevicesMapComponent implements OnInit, OnChanges {
  
  @Input() devices: DevicesLocation[] = [];
  
  constructor(
    public cognitoService: CognitoService,
    @Inject(forwardRef(() => TranslateService)) @Inject(forwardRef(() => TranslateService)) private translate: TranslateService,
    public  theme: ThemeService,
    public clientService: ClientService
  ) {}

  public devicesLocationArray: DevicesLocation[] | undefined = [];
  public selectedClients: string = '';
  public devicesWithGps: any[] = [];
  public filteredDevicesWithGps: any[] = [];
  public originalDevicesWithGps: any[] = [];
  public map: google.maps.Map | null = null;
  public markers: google.maps.marker.AdvancedMarkerElement[] = [];
  
  ngOnInit(): void {
    this.devicesLocationArray = [...this.devices]
  }

  async ngAfterViewInit() {
    await this.createMap();
    if (this.devicesLocationArray && this.devicesLocationArray.length > 0) {
      this.fillMarkersArray();
      this.displayMarkersOnMap();
    }
  }

  // ON Changes lifecycle hook to handle changes in the devices input
  // This will be triggered when the devices input changes
  ngOnChanges(changes: SimpleChanges) {
    // If we have no map initiated, return
    if (!this.map) {
      return;
    }
    // Clear markers from the map if they exist
    if(this.markers && this.markers.length > 0) {
      this.markers.forEach((marker) => { marker.map = null;});
    }
    if (changes['devices'] && this.devices && this.devices.length > 0) {
      this.devicesLocationArray = [...this.devices]
      this.fillMarkersArray();
      this.displayMarkersOnMap();
    }
  }

  // Initialize Google Maps, default gps location is Muirwood
  async createMap(
    gps: { latitude: number; longitude: number } = {
      latitude: 45.65054871734081,
      longitude: -73.84789617258333,
    },
    htmlElementId: string = 'mapAllDevices'
  ): Promise<void> {

    // Null check on map element
    const mapDiv = document.getElementById(htmlElementId);
    if(!mapDiv) {
      console.error(`Element with id ${htmlElementId} not found.`);
      return;
    }

    // Load library only if needed
    const { Map } = (await google.maps.importLibrary(
      'maps'
    )) as google.maps.MapsLibrary;
    const { AdvancedMarkerElement } = (await google.maps.importLibrary('marker')) as google.maps.MarkerLibrary;

    // Create a new Google Map instance
    const map = new Map(mapDiv as HTMLElement, {
      zoom: 10, // 1 : World - 5: Landmass/continent - 10: City - 15: Streets - 20: Buildings
      center: { lat: gps.latitude, lng: gps.longitude },
      mapId: 'ALL_DEVICES_MAP',
      disableDefaultUI: true,
    });
    this.map = map;
  }

  zoomToDevice(device: any) {
    if(this.map && device){
      let gps = []
      if(device.gps != ""){
        gps = device.gps.split(',');
        let position = new google.maps.LatLng(Number(gps[0]), Number(gps[1]));
        this.map.panTo(position);
        this.map.setZoom(18);
      }
    }
  }

  clearAndReapplyMapMarkers() {
    // Clear existing markers on map
    this.markers.forEach((marker) => { marker.map = null;});
    
    this.fillMarkersArray()
    // Reapply markers based on the filtered devices with GPS
    this.displayMarkersOnMap();
  }

  fillMarkersArray() {
    this.markers = []; // Clear existing markers
    if(!this.devicesLocationArray || this.devicesLocationArray.length === 0) {
      console.error('No device(s) to create markers for.');
      return;
    }

    // Iterate through the devices and create markers for each one
    this.devicesLocationArray.forEach((device: any) => {
      const gps = device.gps;
      if (!gps) return; // Skip if no GPS data

      const [lat, lng] = gps.split(',').map(Number);
      const newMarker = new google.maps.marker.AdvancedMarkerElement({
        position: { lat, lng },
        title: device.thingName+", \n" + device.address,
      });
      // Add the new marker to the markers array
      this.markers.push(newMarker);
    });
  }

  displayMarkersOnMap(){
    // Check if the map is initialized
    if (this.map && this.markers.length > 0) {
      // Iterate through the markers and set them on the map
      this.markers.forEach(marker => {
        marker.map = this.map;
      });
    } else {
      console.error('Map or markers not initialized');
    }
  }
}
